/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "First-party tracking", description = "Send browser events through your own domain to improve client-side event coverage.", lastUpdatedDate = new Date(1782432000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "First-party tracking",
        description: "Send browser events through your own domain to improve client-side event coverage.",
        lastUpdatedDate: "Fri Jun 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Proxying",
          "depth": 0,
          "id": "proxying"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Proxying the JavaScript SDK",
          "depth": 1,
          "id": "proxying-the-javascript-sdk"
        }, {
          "value": "Proxying events",
          "depth": 1,
          "id": "proxying-events"
        }, {
          "value": "Intelligent Tracking Prevention",
          "depth": 0,
          "id": "intelligent-tracking-prevention"
        }, {
          "value": "Mitigating ITP",
          "depth": 1,
          "id": "mitigating-itp"
        }, {
          "value": "Client SDK setup",
          "depth": 1,
          "id": "client-sdk-setup"
        }, {
          "value": "Server setup",
          "depth": 1,
          "id": "server-setup"
        }, {
          "value": "An API for creating server and browser cookies",
          "depth": 1,
          "id": "an-api-for-creating-server-and-browser-cookies"
        }, {
          "value": "An API for clearing server cookies",
          "depth": 1,
          "id": "an-api-for-clearing-server-cookies"
        }, {
          "value": "API spec",
          "depth": 1,
          "id": "api-spec"
        }, {
          "value": "Server examples",
          "depth": 1,
          "id": "server-examples"
        }, {
          "value": "More information",
          "depth": 1,
          "id": "more-information"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      a: "a",
      h2: "h2",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span",
      hr: "hr",
      em: "em",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "First-party tracking sends browser events through your own domain instead of directly to Hightouch. This improves event coverage when browsers or privacy tools restrict third-party requests, such as Safari's Intelligent Tracking Prevention (ITP). You set it up by serving the SDK and collecting events from a domain you control."
      }), "\n", _jsx(_components.p, {
        children: "First-party tracking has three parts. The first two are proxies you put in front of Hightouch; the third is a small service you host to issue cookies that survive browser privacy limits:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Proxy the JavaScript SDK"
          }), " so the library loads from your domain instead of ", _jsx(_components.code, {
            children: "cdn.hightouch-events.com"
          }), ". See ", _jsx(_components.a, {
            href: "#proxying-the-javascript-sdk",
            children: "Proxying the JavaScript SDK"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Proxy events"
          }), " so the SDK sends collected events to your domain instead of ", _jsx(_components.code, {
            children: "hightouch-events.com"
          }), ". See ", _jsx(_components.a, {
            href: "#proxying-events",
            children: "Proxying events"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Issue first-party, HTTP-only cookies"
          }), " from a service on your domain so anonymous and user identifiers persist past Safari's cookie limits. See ", _jsx(_components.a, {
            href: "#intelligent-tracking-prevention",
            children: "Intelligent Tracking Prevention"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The first two parts improve delivery against ad blockers and third-party request blocking. The third keeps identity stable for returning visitors on browsers that enforce ITP. You can set them up independently, but most teams that need ITP mitigation do all three."
      }), "\n", _jsx(_components.h2, {
        id: "proxying",
        children: "Proxying"
      }), "\n", _jsx(_components.p, {
        children: "To use first-party tracking, set up a proxy so clients send their event data to your domain rather than directly to Hightouch."
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "You'll need a certificate for whatever domain you plan to use for the proxy."
      }), "\n", _jsxs(_components.p, {
        children: ["This guide uses ", _jsx(_components.a, {
          href: "https://aws.amazon.com/cloudfront",
          children: "Amazon Cloudfront"
        }), ", but any CDN or reverse proxy will work."]
      }), "\n", _jsx(_components.h3, {
        id: "proxying-the-javascript-sdk",
        children: "Proxying the JavaScript SDK"
      }), "\n", _jsx(_components.p, {
        children: "Out of the box, the JavaScript SDK is served from cdn.hightouch-events.com. But you can serve the JavaScript SDK from your own domain instead."
      }), "\n", _jsxs(_components.p, {
        children: ["First, create a ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/distribution-web-creating-console.html",
          children: "Cloudfront distribution"
        }), " with the following settings:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Setting"
            }), _jsx(_components.th, {
              children: "Value"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin domain"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "cdn.hightouch-events.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin path"
            }), _jsx(_components.td, {
              children: "Leave this blank"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Name"
            }), _jsx(_components.td, {
              children: "Give your distribution a unique name"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enable Origin Shield"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "No"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Compress objects automatically"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Yes"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Viewer protocol policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "HTTPS only"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Allowed HTTP methods"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "GET, HEAD"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Restrict viewer access"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "No"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cache key and origin requests"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Cache policy and origin request policy"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cache policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "CachingOptimized"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin request policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AllViewerExceptHostHeader"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Response headers policy"
            }), _jsx(_components.td, {
              children: "None"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Function associations"
            }), _jsx(_components.td, {
              children: "No associations"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Alternate domain name (CNAME)"
            }), _jsx(_components.td, {
              children: "The domain you want to expose to clients"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Custom SSL certificate"
            }), _jsx(_components.td, {
              children: "Select a certificate that corresponds to the CNAME you entered"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Supported HTTP versions"
            }), _jsxs(_components.td, {
              children: ["Enable ", _jsx(_components.code, {
                children: "HTTP/2"
              }), " and ", _jsx(_components.code, {
                children: "HTTP/3"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Default root object"
            }), _jsx(_components.td, {
              children: "Leave this blank"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Standard logging"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Off"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "IPv6"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "On"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Then, point your clients to the CNAME you configured. If using the JavaScript snippet, you can do this by replacing ", _jsx(_components.code, {
          children: "cdn.hightouch-events.com"
        }), " in the body of the snippet with your CNAME."]
      }), "\n", _jsx(_components.h3, {
        id: "proxying-events",
        children: "Proxying events"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, events are sent to ", _jsx(_components.code, {
          children: "hightouch-events.com"
        }), ". But you can configure Hightouch to send events to your own domain instead."]
      }), "\n", _jsxs(_components.p, {
        children: ["First, create a ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/distribution-web-creating-console.html",
          children: "Cloudfront distribution"
        }), " with the following settings:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Setting"
            }), _jsx(_components.th, {
              children: "Value"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin domain"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "us-east-1.hightouch-events.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin path"
            }), _jsx(_components.td, {
              children: "Leave this blank"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Name"
            }), _jsx(_components.td, {
              children: "Give your distribution a unique name"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enable Origin Shield"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "No"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Compress objects automatically"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Yes"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Viewer protocol policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "HTTPS only"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Allowed HTTP methods"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "GET, HEAD, OPTIONS, PUT, POST, PATCH, DELETE"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Restrict viewer access"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "No"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cache key and origin requests"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Cache policy and origin request policy"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cache policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "CachingDisabled"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Origin request policy"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AllViewer"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Response headers policy"
            }), _jsx(_components.td, {
              children: "None"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Function associations"
            }), _jsx(_components.td, {
              children: "No associations"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Alternate domain name (CNAME)"
            }), _jsx(_components.td, {
              children: "The domain you want to expose to clients"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Custom SSL certificate"
            }), _jsx(_components.td, {
              children: "Select a certificate that corresponds to the CNAME you entered"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Supported HTTP versions"
            }), _jsxs(_components.td, {
              children: ["Enable ", _jsx(_components.code, {
                children: "HTTP/2"
              }), " and ", _jsx(_components.code, {
                children: "HTTP/3"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Default root object"
            }), _jsx(_components.td, {
              children: "Leave this blank"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Standard logging"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Off"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "IPv6"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "On"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Then, set your SDK's ", _jsx(_components.code, {
          children: "apiHost"
        }), " setting to the CNAME you configured. For example, if you are using the Browser SDK:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: [_jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), " },\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "apiHost"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"events.example.com\""
          }), " }\n);\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: " "
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.p, {
        children: " "
      }), "\n", _jsx(_components.h2, {
        id: "intelligent-tracking-prevention",
        children: "Intelligent Tracking Prevention"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Intelligent Tracking Prevention"
        }), " (ITP) is an automatic feature of the Safari web browser, designed to limit user tracking. More specifically, the program is designed to prevent analytics libraries from using client-side Javascript to correlate user sessions. ITP configures the Safari browser in the following ways:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Third-party cookies are blocked by default."
        }), "\n", _jsx(_components.li, {
          children: "First-party cookies (and localStorage) have a default expiry of 7 days."
        }), "\n", _jsx(_components.li, {
          children: "A user revisiting a website will restart the 7 day expiry."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Unfortunately, most analytics libraries persist session information by:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Storing an identifier in a Cookie."
        }), "\n", _jsx(_components.li, {
          children: "Storing an identifier in localStorage."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A legacy analytics installation, one not working to mitigate ITP, will collect less accurate session data for Safari users. For example, a single user visiting a website on both 01/01/2023 and 01/15/2023 will look like two different users. After Safari deletes the first cookie on 01/08/2023, the web SDK will be forced to make a new cookie and a new identifier for the session occurring on 01/15/2023. Downstream, your warehouse will likely see these sessions as two different users. This simple inaccuracy may impact otherwise useful metrics generated from analytics tracking."
      }), "\n", _jsx(_components.h3, {
        id: "mitigating-itp",
        children: "Mitigating ITP"
      }), "\n", _jsx(_components.p, {
        children: "When properly configured, Hightouch Events can store session identifiers outside of ITP's 7 day expiry. It does this by using first party, HTTPOnly cookies. These are not subject to Safari's 7 day expiry. These types of cookies are generally used for logging into a website and are less likely to be limited, even when dealing with anonymous users."
      }), "\n", _jsx(_components.p, {
        children: "These \"server cookies\" must follow several rules put in place by ITP:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The server providing the HTTPOnly cookie must be on the same domain as the website."
        }), "\n", _jsx(_components.li, {
          children: "If the server is on a subdomain of the website, its IP address must match the IP address that served the main HTML document."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In practice, this means that ITP mitigation requires customers to host some sort of web service responsible for returning HTTPOnly cookies to the browser. The service does not need to be complex; Hightouch has numerous example servers. The most important part of this ", _jsx(_components.strong, {
          children: "HTTPCookieService"
        }), " is that it must be a first party service: routing a subdomain via DNS will not suffice."]
      }), "\n", _jsxs(_components.p, {
        children: ["In order for the HTTPCookieService to serve first party, HTTPOnly cookies, you'll need ", _jsx(_components.strong, {
          children: _jsx(_components.em, {
            children: "one of"
          })
        }), " the following:"]
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "webserver"
        }), " that handles both your HTML documents and your API. This would mean building the HTTPCookieService functionality into your regular webserver. As an example, you might already have a Java Spring server for handling regular website requests."]
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "reverse proxy"
        }), " that can forward path-dependent requests to different servers. This requires proxying your domain to both an HTTPCookieService and your regular webserver. As an example, you might already have something like NGINX that proxies HTML requests to one place and API requests to another."]
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "CDN"
        }), " that can run programmatic logic when matching certain path-dependent requests. If you already have a CDN, you may be able to add custom handlers without standing up new servers. This can be achieved with a variety of technologies like Amazon's Cloudfront, Lambda@Edge, or API Gateway. Cloudflare Workers can also work."]
      }), "\n", _jsx(_components.h3, {
        id: "client-sdk-setup",
        children: "Client SDK setup"
      }), "\n", _jsx(_components.p, {
        children: "Once your HTTPCookieService is reachable on your domain, you may configure your Hightouch Events SDK to make use of it when generating Cookies:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-Javascript",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'@ht-sdks/events-sdk-js-browser'"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " htevents = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEventsBrowser"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "load"
          }), "(\n  { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'<YOUR_WRITE_KEY>'"
          }), "},\n  {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "apiHost"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"us-east-1.hightouch-events.com\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// HtEvents API remains the same"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "httpCookieServiceOptions"
          }), ": {\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "clearUrl"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'/ht/clear'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// route hosted on *your* domain and infra"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "renewUrl"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'/ht/renew'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// route hosted on *your* domain and infra"
          }), "\n    }\n  },\n)\n\nhtevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hello world'"
          }), ")\n\n", _jsx(_components.span, {
            className: "hljs-variable language_",
            children: "document"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), "?.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "addEventListener"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'click'"
          }), ", ", _jsx(_components.span, {
            className: "hljs-function",
            children: "() =>"
          }), " {\n  htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'documentBodyClick'"
          }), ")\n})\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "server-setup",
        children: "Server setup"
      }), "\n", _jsx(_components.p, {
        children: "The Events SDK expects to interact with a customer's HTTPCookieService that implements a specific spec for two routes. You can name the endpoints whatever you want, as long as you configure the client SDK to call those endpoints."
      }), "\n", _jsx(_components.h3, {
        id: "an-api-for-creating-server-and-browser-cookies",
        children: "An API for creating server and browser cookies"
      }), "\n", _jsxs(_components.p, {
        children: ["This route should look for the following ", _jsx(_components.strong, {
          children: "browser"
        }), " cookies (from Events SDK):"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "request.headers.get(\"Cookie\")[\"htjs_anonymous_id\"]"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "request.headers.get(\"Cookie\")[\"htjs_user_id\"]"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This route should return these values as ", _jsx(_components.strong, {
          children: "server"
        }), " cookies:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "response.cookie(\"htjs_anonymous_id_srvr\", anonVal, {httpOnly:true, ...})"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "response.cookie(\"htjs_user_id_srvr\", userIdVal, {httpOnly:true, ...})"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If there are no browser cookies found, return any server cookies as ", _jsx(_components.strong, {
          children: "browser"
        }), " cookies:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "anonVal = request.headers.get(\"Cookie\")[\"htjs_anonymous_id_srvr\"]"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "userIdVal = request.headers.get(\"Cookie\")[\"htjs_user_id_srvr\"]"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "response.cookie(\"htjs_anonymous_id\", anonVal, ...)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "response.cookie(\"htjs_user_id\", userIdVal, ...)"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "an-api-for-clearing-server-cookies",
        children: "An API for clearing server cookies"
      }), "\n", _jsxs(_components.p, {
        children: ["This route should look for ", _jsx(_components.strong, {
          children: "server"
        }), " cookies and clean them:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "res.cookie(\"htjs_anonymous_id_srvr\", \"\", {maxAge: 0, httpOnly:true, ...});"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "res.cookie(\"htjs_user_id_srvr\", \"\", {maxAge: 0, httpOnly:true, ...});"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "api-spec",
        children: "API spec"
      }), "\n", _jsxs(_components.p, {
        children: ["The spec of the actual ", _jsx(_components.code, {
          children: "request"
        }), " and ", _jsx(_components.code, {
          children: "response"
        }), " payloads are kept intentionally vague. The spec should fit a variety of server environments."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Events SDK only requires that the server: A) handles cookies and B) returns a ", _jsx(_components.code, {
          children: "200"
        }), " status code."]
      }), "\n", _jsx(_components.h3, {
        id: "server-examples",
        children: "Server examples"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-js-mono/blob/master/packages/browser/src/core/http-cookies/server-examples/node-express-js.md",
            children: "Express.js and NGINX"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-js-mono/blob/master/packages/browser/src/core/http-cookies/server-examples/node-next-js.md",
            children: "Next.js and Vercel"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://github.com/ht-sdks/events-sdk-js-mono/blob/master/packages/browser/src/core/http-cookies/server-examples/node-aws-lambda.md",
            children: "AWS Lambda and API Gateway"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "more-information",
        children: "More information"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Safari: ", _jsx(_components.a, {
            href: "https://webkit.org/blog/9521/intelligent-tracking-prevention-2-3/",
            children: "https://webkit.org/blog/9521/intelligent-tracking-prevention-2-3/"
          })]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
